import React, { useEffect, useRef, useState } from "react";

import { EmergencySituationType } from "../../../types/emergency";

import NaverMap from "../NaverMap";
import { LocationResult } from "../../../hooks/useGPSInstant";
import clsx from "clsx";
import { Coordinates } from "../../../types/location";
import PrimaryButton from "../../../components/Button/Primary";

type Props = {
  situation?: EmergencySituationType;
  unUseBottomText?: boolean;
  size?: MAP_SIZE_TYPE;
  cert: boolean;
  callGps?: () => void;
  gpsResult?: LocationResult;
  handleClickShelterImg?: (e: React.MouseEvent) => void;
};

const GPS_CALL_INTERVAL = 3000;
const RETRY_INTERVAL = 5000;
export type MAP_SIZE_TYPE = "default" | "full";

export default function NaverMapWithSkeleton({
  situation,
  unUseBottomText,
  size = "default",
  cert,
  callGps,
  gpsResult,
  handleClickShelterImg,
}: Props) {
  const gpsCallInterval = useRef<NodeJS.Timer | null>(null);
  const gpsRetryTimeout = useRef<NodeJS.Timer | null>(null);
  const gpsWatchId = useRef<number | null>(null);
  const [myCoordinate, setMyCoordinate] = useState<Coordinates | undefined>(
    undefined
  );
  const [popup, setPopup] = useState<"extendMap" | "">("");

  const height = size === "default" ? "h-[64vw]" : "h-screen";

  const invisible = popup === "extendMap" ? "invisible" : "";

  useEffect(() => {
    if (popup === "") {
      if (cert && callGps) {
        gpsCallInterval.current = setInterval(() => {
          callGps();
        }, GPS_CALL_INTERVAL);

        return () => {
          if (gpsCallInterval.current) {
            clearInterval(gpsCallInterval.current);
          }
        };
      } else if (!cert) {
        getLocation();

        return () => {
          if (gpsWatchId.current) {
            navigator.geolocation.clearWatch(gpsWatchId.current);
          }
          if (gpsRetryTimeout.current) {
            clearTimeout(gpsRetryTimeout.current);
          }
        };
      }
    } else if (popup === "extendMap") {
      if (gpsCallInterval.current) {
        clearInterval(gpsCallInterval.current);
      }
      if (gpsWatchId.current) {
        navigator.geolocation.clearWatch(gpsWatchId.current);
      }
      if (gpsRetryTimeout.current) {
        clearTimeout(gpsRetryTimeout.current);
      }
    }
  }, [popup, cert, callGps]);

  const getLocation = () => {
    if (navigator.geolocation) {
      gpsWatchId.current = navigator.geolocation.watchPosition(
        (pos) => {
          setMyCoordinate({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
          if (gpsRetryTimeout.current) {
            clearTimeout(gpsRetryTimeout.current);
          }
        },
        (error) => {
          console.error(
            "위치정보를 얻는데에 실패하였습니다. 다시 시도합니다. :",
            error
          );

          // 타임아웃이 발생하거나 위치 정보를 얻지 못한 경우 재시도
          gpsRetryTimeout.current = setTimeout(getLocation, RETRY_INTERVAL); // 5초 후 재시도
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 5000,
        }
      );
    }
  };

  useEffect(() => {
    if (
      gpsResult &&
      gpsResult.result &&
      !gpsResult.result?.error &&
      gpsResult.isLoading === false
    ) {
      setMyCoordinate({
        lat: gpsResult.result.lat,
        lng: gpsResult.result.lng,
      });
    }
  }, [gpsResult]);

  return (
    <>
      {situation ? (
        <>
          <div
            className={clsx("w-full", height, invisible)}
            data-testid="naver-map-wrapper-div"
            onClick={
              size === "full"
                ? () => {}
                : () => {
                    setPopup("extendMap");
                  }
            }
          >
            <NaverMap
              shelter={situation}
              myCoordinate={myCoordinate}
              size={size}
              handleClickShelterImg={handleClickShelterImg}
            />
          </div>

          {!unUseBottomText && (
            <div className="flex flex-row p-5 pt-[30px]">
              <div className="flex flex-col gap-2.5 items-start justify-center">
                <div className="leading-[26px]">
                  <span className="font-semibold">
                    {situation?.incidentAddress} {situation?.incidentFloorName}
                  </span>
                  에서{" "}
                  <span className="font-semibold">
                    {situation?.evacuationCategory}
                  </span>
                  가 발생했습니다.
                </div>
                <div className="leading-[29px] text-2xl font-semibold">
                  <span className="text-brand-primary-magenta">
                    {situation?.shelterName}
                  </span>{" "}
                  으로 대피해주세요.
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div data-testid="situation-skeleton-wrapper">
          <div className="w-full h-[64vw] animate-pulse bg-gray-400"></div>
          {!unUseBottomText && (
            <div className="flex flex-row p-5 pt-[30px]">
              <div className="flex flex-col gap-2.5 items-start justify-center">
                <div className="w-[calc(100vw-40px)] h-[20.5px] animate-pulse bg-gray-400 rounded-full"></div>
                <div className="w-[50vw] h-[20.5px] animate-pulse bg-gray-400 rounded-full"></div>
                <div className="w-[calc(100vw-40px)] h-[24.5px] animate-pulse bg-gray-400 rounded-full"></div>
                <div className="w-[50vw] h-[24.5px] animate-pulse bg-gray-400 rounded-full"></div>
              </div>
            </div>
          )}
        </div>
      )}
      {popup === "extendMap" && (
        <div className="fixed inset-0 flex items-end justify-center z-50 w-full h-full">
          <NaverMapWithSkeleton
            situation={situation}
            unUseBottomText={true}
            size="full"
            cert={cert}
            callGps={callGps}
            gpsResult={gpsResult}
          />
          <PrimaryButton
            className="absolute bottom-5 z-10 rounded-[10px] left-1/2 -translate-x-1/2 w-[calc(100%-40px)] shadow-md h-[50px]"
            onClick={() => setPopup("")}
          >
            닫기
          </PrimaryButton>
        </div>
      )}
    </>
  );
}
